import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { companyInfo } from "@/mocks/help-center";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const EMPTY_FORM: FormData = { name: "", email: "", subject: "", message: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(formData: FormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!formData.name.trim()) {
    errors.name = "Name is required";
  } else if (formData.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters";
  }

  if (!formData.email.trim()) {
    errors.email = "Email is required";
  } else if (!EMAIL_RE.test(formData.email)) {
    errors.email = "Please enter a valid email address";
  }

  if (!formData.subject.trim()) {
    errors.subject = "Subject is required";
  } else if (formData.subject.trim().length < 3) {
    errors.subject = "Subject must be at least 3 characters";
  }

  if (!formData.message.trim()) {
    errors.message = "Message is required";
  } else if (formData.message.trim().length < 20) {
    errors.message = "Message must be at least 20 characters";
  }

  return errors;
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setServerError(false);

    const payload = {
      email: formData.email,
      text: `Name: ${formData.name}\n\n${formData.message}`,
      problemType: "Contact Support",
      to_email: "support@vantorylabs.com",
      subject: formData.subject,
    };

    try {
      await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch {
      setServerError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-ice">
      <header className="bg-white/70 backdrop-blur-md border-b border-cream-dark/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-5">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-primary hover:opacity-70 transition-opacity cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ice">
              <i className="ri-arrow-left-line text-xl text-primary" />
            </div>
            <span className="font-semibold text-sm md:text-base">
              Back to Help Center
            </span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-3 tracking-tight font-heading">
            CONTACT SUPPORT
          </h1>
          <div className="w-12 h-1 bg-accent mb-4 rounded-full" />
          <p className="text-base md:text-lg text-gray-500 max-w-2xl">
            Connect directly with our support specialists. Fill out the form
            below and we will get back to you within 24 hours.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,64,143,0.06)] border border-cream-dark/40 p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-2xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-primary mb-2 font-heading">
              Message Sent
            </h2>
            <p className="text-gray-500 mb-6">
              Thank you for reaching out. Our support team will respond within 24
              hours.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData(EMPTY_FORM);
              }}
              className="bg-primary text-white px-6 py-3 font-semibold text-sm hover:bg-primary/90 transition-colors rounded-full whitespace-nowrap cursor-pointer"
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,64,143,0.06)] border border-cream-dark/40 p-6 md:p-10"
          >
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors bg-cream/40 ${
                      errors.name
                        ? "border-red-400 focus:border-red-400"
                        : "border-cream-dark/60 focus:border-primary"
                    }`}
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors bg-cream/40 ${
                      errors.email
                        ? "border-red-400 focus:border-red-400"
                        : "border-cream-dark/60 focus:border-primary"
                    }`}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors bg-cream/40 ${
                    errors.subject
                      ? "border-red-400 focus:border-red-400"
                      : "border-cream-dark/60 focus:border-primary"
                  }`}
                  placeholder="What is this regarding?"
                  value={formData.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                />
                {errors.subject && (
                  <p className="text-xs text-red-500 mt-1">{errors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Message
                </label>
                <textarea
                  rows={6}
                  maxLength={500}
                  className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors resize-none bg-cream/40 ${
                    errors.message
                      ? "border-red-400 focus:border-red-400"
                      : "border-cream-dark/60 focus:border-primary"
                  }`}
                  placeholder="Describe your issue in detail..."
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.message ? (
                    <p className="text-xs text-red-500">{errors.message}</p>
                  ) : (
                    <span className="text-xs text-gray-400">
                      Minimum 20 characters required
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">
                    {formData.message.length}/500
                  </span>
                </div>
              </div>

              {serverError && (
                <p className="text-sm text-red-500">
                  Something went wrong. Please try again later.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-primary text-white px-8 py-4 font-semibold text-sm hover:bg-primary/90 transition-colors rounded-full whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Get Support"}
              </button>
            </div>
          </form>
        )}
      </main>

      <footer className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
          <p className="text-sm text-white/60">{companyInfo.name} — Help Center</p>
        </div>
      </footer>
    </div>
  );
}
